import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
  MenuItem, LinearProgress, TablePagination, Stack, Card, CardContent,
  useTheme, useMediaQuery, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Alert, CircularProgress,
} from '@mui/material';
import { Search, DriveFileMove } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { operationTaskService, projectService, farmOperationService } from '@smartgrow/shared';
import type { OperationTask, TaskStatus } from '@smartgrow/shared';

const STATUS_COLORS: Record<TaskStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  SCHEDULED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  SKIPPED: 'default',
  CANCELLED: 'error',
};

const fmt = (n?: number) => n == null ? '—' : `₦${n.toLocaleString('en-NG')}`;

export const OperationTasksPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [assigneeTypeFilter, setAssigneeTypeFilter] = useState<string>('ALL');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [page, setPage] = useState(0);
  const [moveTarget, setMoveTarget] = useState<OperationTask | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', selectedProjectId],
    queryFn: () => farmOperationService.getByProject(selectedProjectId),
    enabled: !!selectedProjectId,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['operation-tasks', page, selectedProjectId, selectedOperationId, statusFilter, assigneeTypeFilter, debouncedSearch],
    queryFn: () => operationTaskService.getAll({
      page,
      size: 10,
      projectId: selectedProjectId || undefined,
      farmOperationId: selectedOperationId || undefined,
      search: debouncedSearch || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      assigneeType: assigneeTypeFilter === 'ALL' ? undefined : assigneeTypeFilter,
    }),
  });

  const filtered: OperationTask[] = data?.content ?? [];

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setSelectedOperationId('');
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Operation Tasks</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Project" value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Operation" value={selectedOperationId}
            onChange={(e) => { setSelectedOperationId(e.target.value); setPage(0); }} disabled={!selectedProjectId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Operations</MenuItem>
            {(operationsData?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="SKIPPED">Skipped</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </TextField>
          <TextField select size="small" label="Assignee Type" value={assigneeTypeFilter}
            onChange={(e) => setAssigneeTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="STAFF">Staff</MenuItem>
            <MenuItem value="CONTRACTOR">Contractor</MenuItem>
            <MenuItem value="EXTERNAL">External</MenuItem>
            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
          </TextField>
        </Box>
        <TextField fullWidth placeholder="Search by task title, operation, or project..." value={search}
          onChange={(e) => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {isMobile ? (
        <Stack spacing={2}>
          {!isLoading && filtered.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No tasks found</Typography>
          )}
          {filtered.map((t: OperationTask) => (
            <Card key={t.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${t.farmOperationId}`)}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip label={t.status} color={STATUS_COLORS[t.status]} size="small" />
                    <IconButton size="small" title="Move to another operation" onClick={(e) => { e.stopPropagation(); setMoveTarget(t); }}>
                      <DriveFileMove fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  <Typography component="span" variant="caption"
                    sx={{ '&:hover': { textDecoration: 'underline' } }}
                    onClick={(e) => { e.stopPropagation(); t.projectId && navigate(`/dashboard/projects/${t.projectId}`); }}
                  >
                    {t.projectName}
                  </Typography>
                  {' — '}{t.farmOperationName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Chip label={t.assigneeType} variant="outlined" size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {t.scheduledDate ? new Date(t.scheduledDate).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Task</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Operation</strong></TableCell>
                <TableCell><strong>Assignee</strong></TableCell>
                <TableCell><strong>Scheduled</strong></TableCell>
                <TableCell><strong>Est. Cost</strong></TableCell>
                <TableCell><strong>Actual Cost</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No tasks found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((t: OperationTask, i: number) => (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${t.farmOperationId}`)}>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.taskType}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2"
                      sx={{ '&:hover': { textDecoration: 'underline' } }}
                      onClick={(e) => { e.stopPropagation(); t.projectId && navigate(`/dashboard/projects/${t.projectId}`); }}
                    >
                      {t.projectName ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{t.farmOperationName ?? '—'}</TableCell>
                  <TableCell>
                    <Chip label={t.assigneeType} variant="outlined" size="small" />
                    {(t.assignedStaffName || t.assignedContractorName) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t.assignedStaffName || t.assignedContractorName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{t.scheduledDate ? new Date(t.scheduledDate).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{fmt(t.estimatedCost)}</TableCell>
                  <TableCell>{fmt(t.actualCost)}</TableCell>
                  <TableCell>
                    <Chip label={t.status} color={STATUS_COLORS[t.status]} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Move to another operation" onClick={(e) => { e.stopPropagation(); setMoveTarget(t); }}>
                      <DriveFileMove fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}

      {moveTarget && (
        <MoveTaskDialog
          task={moveTarget}
          onClose={() => setMoveTarget(null)}
          onMoved={() => { qc.invalidateQueries({ queryKey: ['operation-tasks'] }); setMoveTarget(null); }}
        />
      )}
    </Box>
  );
};

const MoveTaskDialog = ({ task, onClose, onMoved }: { task: OperationTask; onClose: () => void; onMoved: () => void }) => {
  const [projectId, setProjectId] = useState(task.projectId ?? '');
  const [operationId, setOperationId] = useState('');
  const [error, setError] = useState('');

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', projectId],
    queryFn: () => farmOperationService.getByProject(projectId),
    enabled: !!projectId,
  });

  const moveMutation = useMutation({
    mutationFn: () => farmOperationService.moveTask(task.id, operationId),
    onSuccess: onMoved,
    onError: () => setError('Failed to move task. Please try again.'),
  });

  const targetOperations = (operationsData?.content ?? []).filter((op) => op.id !== task.farmOperationId);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move Task</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Moving "{task.title}" from <strong>{task.farmOperationName}</strong> ({task.projectName}).
          Everything about the task — schedule, assignee, cost — carries over unchanged.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField select fullWidth label="Target Project" value={projectId}
          onChange={(e) => { setProjectId(e.target.value); setOperationId(''); }} sx={{ mb: 2 }}>
          {(projectsData?.content ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>
        <TextField select fullWidth label="Target Operation" value={operationId}
          onChange={(e) => setOperationId(e.target.value)} disabled={!projectId}>
          {targetOperations.length === 0 && <MenuItem value="" disabled>No other operations in this project</MenuItem>}
          {targetOperations.map((op) => (
            <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!operationId || moveMutation.isPending}
          onClick={() => moveMutation.mutate()}
        >
          {moveMutation.isPending ? <CircularProgress size={20} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
